import { useEffect, useMemo, useState, useRef, lazy, Suspense } from 'react';
import { Layout, Space, Badge, Grid, Drawer, Dropdown, Avatar, Input } from 'antd';

const loadCheckoutModule = () => import('@features/checkout/CheckoutPage.jsx');
const CheckoutPage = lazy(loadCheckoutModule);
import {
  DownOutlined,
  EnvironmentFilled,
  UserOutlined,
  ShoppingCartOutlined,
  HeartOutlined,
  DeleteOutlined,
  EditOutlined,
  LogoutOutlined,
  HomeOutlined,
  HomeFilled,
  ReadOutlined,
  AppstoreOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@core/hooks/useAuth';
import { useAccountSelectionStore } from '@core/store/accountSelectionStore';
import {
  useActiveCart,
  useClearCart,
  useRemoveCartLine,
  useUpdateCartLine,
} from '@core/hooks/useCart';
import { useWishlistStore } from '@core/store/wishlistStore';
import { useRuntimeBrand } from '@core/hooks/useRuntimeBrand';
import { getAvatarStyles } from '@core/utils/avatar';
import { useCoffeePlaceholderSrc } from '@core/utils/coffeePlaceholder';
import { useLocations } from '@core/hooks/useLocations';
import { useStoreContext } from '@core/hooks/useStoreContext';
import { useCartLineNote } from '@core/hooks/useCartLineNote';
import { useUiStore } from '@core/store/uiStore';
import { useStoreClosed } from '@core/utils/storeStatus';
import Button from '@components/base/Button';
import CartLineDetails from '@components/base/CartLineDetails';
import CartLineNote from '@components/base/CartLineNote';
import ImageWithFallback from '@components/base/ImageWithFallback';
import ProductDetailView from '@features/product-detail/ProductDetailView.jsx';
import StorefrontAuthModal from '@components/base/StorefrontAuthModal';
import Price from '@components/base/Price';
import EmptyState from '@components/base/EmptyState';
import AppModal from '@components/base/AppModal';
import { useRetainedWhileClosing } from '@components/base/AppModal/useRetainedWhileClosing';
import { useCopy } from '@/app/providers/useCopy';
import { useIndustry } from '@/app/providers/industry-context.js';
import { useAppLanguage } from '@/app/providers/useAppLanguage.js';
import { useProfileDetails } from '@/features/account/AccountPage';

const { Header } = Layout;
const CURRENT_LOCATION_ID = 'temp-current-location';
const fallbackCoffeeLocations = [
  { id: 'temp-coffee-chennai', name: 'Chennai', city: 'Chennai', state: 'Tamil Nadu' },
  { id: 'temp-coffee-coimbatore', name: 'Coimbatore', city: 'Coimbatore', state: 'Tamil Nadu' },
  { id: 'temp-coffee-madurai', name: 'Madurai', city: 'Madurai', state: 'Tamil Nadu' },
];

const fallbackNav = [
  { label: 'Home', to: '/' },
  { label: 'Shop', to: '/catalog' },
  { label: 'Categories', to: '/categories' },
  { label: 'My Orders', to: '/orders' },
];
const heroNav = [
  { label: 'Home', to: '/' },
  { label: 'Menu', to: '/catalog' },
  { label: 'Categories', to: '/categories' },
  { label: 'My Orders', to: '/orders' },
];
// Legacy key from the removed dark/light toggle; cleared on load.
const COFFEE_MODE_STORAGE_KEY = 'coffee-theme-mode';

export default function HeaderCoffee() {
  const t = useCopy();
  const { config } = useIndustry();
  useAppLanguage();
  const { logoUrl: runtimeLogoUrl, brandName: runtimeBrandName } = useRuntimeBrand();
  const coffeePlaceholderSrc = useCoffeePlaceholderSrc();
  const { customer, isAuthenticated, logout } = useAuth();
  const openAccountSelector = useAccountSelectionStore((s) => s.openAccountSelector);
  const { cart, lineCount } = useActiveCart();
  const clearCart = useClearCart();
  const updateCartLine = useUpdateCartLine();
  const { saveNote, isSavingNote } = useCartLineNote();
  const removeCartLine = useRemoveCartLine();
  const wishlistCount = useWishlistStore((s) => s.items.length);
  const navigate = useNavigate();
  const location = useLocation();
  const { pathname, search } = location;
  const isHomePage = pathname === '/';
  const screens = Grid.useBreakpoint();
  const isMobile = !screens.sm;
  const showMobileTabs = !screens.sm;
  const isCartDrawerViewport = Boolean(screens.sm);
  const [authMode, setAuthMode] = useState(null);
  const [authRedirect, setAuthRedirect] = useState(null);
  const [cartOpen, setCartOpen] = useState(false);
  const [checkoutOpen, setCheckoutOpen] = useState(false);
  const [editingCartLine, setEditingCartLine] = useState(null);
  const [shownCartLine, releaseCartLine] = useRetainedWhileClosing(editingCartLine);
  const [showAllCartLines, setShowAllCartLines] = useState(false);
  const [mobileSearchOpen, setMobileSearchOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const { displayName } = useProfileDetails();

  const [searchText, setSearchText] = useState('');
  const searchTimeoutRef = useRef(null);

  const isSearchAllowedPage =
    pathname === '/' ||
    pathname === '/catalog' ||
    pathname === '/categories' ||
    pathname.startsWith('/catalog') ||
    pathname.startsWith('/categories');
  const shouldHideHeaderSearch = !isSearchAllowedPage;

  const onSearch = (value) => {
    if (shouldHideHeaderSearch) return;
    const q = value.trim();
    navigate(q ? `/catalog?q=${encodeURIComponent(q)}` : '/catalog');
  };

  useEffect(() => {
    const query = new URLSearchParams(search).get('q') || '';
    setSearchText(query);
  }, [search]);

  const { data: locations = [] } = useLocations();
  const { locationId } = useStoreContext();
  const openStoreSelector = useUiStore((s) => s.openStoreSelector);

  const selectableLocations = locations.length ? locations : fallbackCoffeeLocations;
  const selectedLocation =
    locationId === CURRENT_LOCATION_ID
      ? { id: CURRENT_LOCATION_ID, name: 'Current location', city: 'Current location' }
      : selectableLocations.find((loc) => loc.id === locationId);
  const activeLocation = selectedLocation ?? selectableLocations[0];
  const { checkStoreOpenOrShowModal } = useStoreClosed();
  const selectedLocationLabel =
    activeLocation?.name ||
    [activeLocation?.city, activeLocation?.state].filter(Boolean).join(', ') ||
    '';
  const hasSelectedMobileLocation = Boolean(selectedLocation);
  const mobileLocationLabel =
    selectedLocation?.name ||
    [selectedLocation?.city, selectedLocation?.state].filter(Boolean).join(', ');
  const navItems = useMemo(() => {
    if (isHomePage) return heroNav;
    const configured = config?.nav?.primary ?? [];
    const merged = [
      { label: 'Home', to: '/' },
      { label: 'Menu', to: '/catalog' },
      { label: 'Categories', to: '/categories' },
      { label: 'My Orders', to: '/orders' },
      ...configured,
    ];
    const deduped = new Map();
    merged.forEach((item) => {
      if (!deduped.has(item.to)) deduped.set(item.to, item);
    });
    return [...deduped.values()].length ? [...deduped.values()] : fallbackNav;
  }, [config?.nav?.primary, isHomePage]);

  const mobileTabs = [
    {
      key: 'home',
      to: '/',
      label: t('home', 'Home'),
      icon: <HomeOutlined />,
      activeIcon: <HomeFilled />,
    },
    {
      key: 'category',
      to: '/categories',
      label: t('category', 'Category'),
      icon: <AppstoreOutlined />,
    },
    {
      key: 'favorites',
      to: '/wishlist',
      label: t('favorites', 'Favorites'),
      icon: <HeartOutlined />,
    },
    { key: 'orders', to: '/orders', label: t('orders', 'Orders'), icon: <ReadOutlined /> },
  ];

  const isTabActive = (tab) => {
    if (tab.key === 'home') return pathname === '/';
    if (tab.key === 'categories') return pathname.startsWith('/categories');
    if (tab.key === 'favorites') return pathname.startsWith('/wishlist');
    return pathname.startsWith(tab.to);
  };

  const accountItems = [
    { key: 'email', label: customer?.email ?? displayName, disabled: true },
    { type: 'divider' },
    { key: 'profile', icon: <UserOutlined />, label: t('profile', 'Profile') },
    // Switch Account hidden for the coffee theme.
    // {
    //   key: 'switch_account',
    //   icon: <UserSwitchOutlined />,
    //   label: t('switchAccount', 'Switch Account'),
    // },
    { type: 'divider' },
    { key: 'logout', icon: <LogoutOutlined />, label: t('signOut', 'Sign Out') },
  ];

  const onAccountClick = ({ key }) => {
    if (key === 'profile') navigate('/account');
    if (key === 'switch_account') openAccountSelector();
    if (key === 'orders') navigate('/orders');
    if (key === 'logout') {
      logout();
      navigate('/');
    }
  };

  const onOrdersClick = () => {
    if (isAuthenticated) {
      navigate('/orders');
      return;
    }
    setAuthRedirect('/orders');
    setAuthMode('login');
  };

  useEffect(() => {
    if (cartOpen) {
      document.body.classList.add('cart-drawer-open');
      document.documentElement.classList.add('cart-drawer-open');
    } else {
      document.body.classList.remove('cart-drawer-open');
      document.documentElement.classList.remove('cart-drawer-open');
    }
    return () => {
      document.body.classList.remove('cart-drawer-open');
      document.documentElement.classList.remove('cart-drawer-open');
    };
  }, [cartOpen]);

  useEffect(() => {
    if (!cartOpen) setShowAllCartLines(false);
  }, [cartOpen]);

  useEffect(() => {
    if (!isMobile) setShowAllCartLines(false);
  }, [isMobile]);

  useEffect(() => {
    if (location.state?.showOrderConfirmation) {
      setCheckoutOpen(false);
      setCartOpen(false);
      return;
    }
    if (!location.state?.openCartDrawer && !location.state?.openCheckoutModal) return;
    if (location.state?.openCartDrawer) setCartOpen(true);
    if (location.state?.openCheckoutModal) setCheckoutOpen(true);
    navigate(`${pathname}${search}`, { replace: true, state: null });
  }, [location.state, navigate, pathname, search]);

  useEffect(() => {
    const handleOpenAuthModal = (e) => {
      const { mode = 'login', redirect = null } = e.detail || {};
      setAuthMode(mode);
      if (redirect) setAuthRedirect(redirect);
    };
    const handleOpenCartDrawer = () => {
      setCartOpen(true);
    };
    const handleOpenCheckoutModal = () => {
      setCheckoutOpen(true);
    };
    const handleCloseCheckoutModal = () => {
      setCheckoutOpen(false);
      setCartOpen(false);
    };
    window.addEventListener('open-auth-modal', handleOpenAuthModal);
    window.addEventListener('open-cart-drawer', handleOpenCartDrawer);
    window.addEventListener('open-checkout-modal', handleOpenCheckoutModal);
    window.addEventListener('close-checkout-modal', handleCloseCheckoutModal);
    return () => {
      window.removeEventListener('open-auth-modal', handleOpenAuthModal);
      window.removeEventListener('open-cart-drawer', handleOpenCartDrawer);
      window.removeEventListener('open-checkout-modal', handleOpenCheckoutModal);
      window.removeEventListener('close-checkout-modal', handleCloseCheckoutModal);
    };
  }, []);

  const cartItems = cart?.items ?? [];
  const isCartEmpty = cartItems.length === 0;
  const totalItemQuantity = cartItems.reduce((sum, line) => sum + (line.quantity ?? 0), 0);
  const shouldCollapseCartLines = !isCartDrawerViewport;
  const visibleCartItems =
    shouldCollapseCartLines && !showAllCartLines ? cartItems.slice(0, 4) : cartItems;
  const hasHiddenCartLines = shouldCollapseCartLines && cartItems.length > 4;

  const [isCheckoutLoading, setIsCheckoutLoading] = useState(false);

  useEffect(() => {
    if (cartOpen) {
      loadCheckoutModule();
    }
  }, [cartOpen]);

  const openCheckout = async () => {
    if (isCartEmpty) return;
    if (!checkStoreOpenOrShowModal()) return;
    if (isAuthenticated) {
      setIsCheckoutLoading(true);
      try {
        await loadCheckoutModule();
      } catch (err) {
        console.error('Failed to load checkout module:', err);
      } finally {
        setIsCheckoutLoading(false);
      }
      setCartOpen(false);
      setCheckoutOpen(true);
      return;
    }
    setAuthRedirect('__checkout_modal__');
    setAuthMode('login');
  };
  // Coffee is light-only. The attribute stays because light-mode styles key off it.
  // An admin-set background colour is applied through --runtime-color-background.
  useEffect(() => {
    document.documentElement.dataset.coffeeMode = 'light';
    try {
      window.localStorage.removeItem(COFFEE_MODE_STORAGE_KEY);
    } catch {
      // Storage unavailable (private mode) — nothing to clean up.
    }
  }, []);

  useEffect(() => {
    const handleScroll = (event) => {
      const targetScrollTop = event?.target instanceof Element ? event.target.scrollTop || 0 : 0;
      const scrollTop = Math.max(
        window.scrollY || 0,
        document.documentElement?.scrollTop || 0,
        document.body?.scrollTop || 0,
        targetScrollTop,
      );
      setIsScrolled(scrollTop > 20);
    };
    handleScroll();
    window.addEventListener('scroll', handleScroll, { passive: true });
    document.addEventListener('scroll', handleScroll, { passive: true, capture: true });
    return () => {
      window.removeEventListener('scroll', handleScroll);
      document.removeEventListener('scroll', handleScroll, { capture: true });
    };
  }, []);

  return (
    <>
      <Header
        className={`coffee-header ${isHomePage ? 'coffee-header--hero' : ''} ${isScrolled ? 'is-scrolled' : ''}`}
      >
        <div className={`coffee-header-main ${isScrolled ? 'is-scrolled' : ''}`}>
          <div
            className={`coffee-header-main__inner ${isMobile ? 'is-mobile' : ''} ${shouldHideHeaderSearch ? 'hide-search' : ''}`}
          >
            {/* Logo – always first */}
            <Link to="/" className="coffee-brand coffee-header-brand" aria-label="Home">
              {runtimeLogoUrl || config?.logoUrl ? (
                <ImageWithFallback
                  src={runtimeLogoUrl || config.logoUrl}
                  alt={runtimeBrandName || config?.name || 'Store Logo'}
                  fit="contain"
                  style={{
                    maxWidth: '7.5rem',
                    objectFit: 'contain',
                    background: 'transparent',
                  }}
                />
              ) : (
                <span className="coffee-brand-mark" aria-hidden>
                  ☕
                </span>
              )}
            </Link>

            {!isMobile ? (
              <>
                <button
                  type="button"
                  className="coffee-delivery-pill"
                  aria-label={t('changeStore', 'Change store')}
                  onClick={openStoreSelector}
                >
                  <EnvironmentFilled className="coffee-delivery-pill__pin" aria-hidden="true" />
                  {selectedLocation ? (
                    <span>
                      <strong>{selectedLocationLabel}</strong>
                    </span>
                  ) : null}
                  <DownOutlined className="coffee-delivery-pill__chevron" aria-hidden="true" />
                </button>

                <nav className="coffee-nav">
                  {navItems.map((item) => {
                    const active =
                      item.to === '/'
                        ? pathname === '/'
                        : pathname === item.to || pathname.startsWith(`${item.to}/`);
                    return (
                      <Link
                        key={item.to}
                        to={item.to}
                        className={active ? 'is-active' : undefined}
                        onClick={(e) => {
                          if (item.to === '/orders' && !isAuthenticated) {
                            e.preventDefault();
                            onOrdersClick();
                          }
                        }}
                      >
                        {t(item.label, t(item.label.toLowerCase().replace(/\s+/g, ''), item.label))}
                      </Link>
                    );
                  })}
                </nav>

                {!shouldHideHeaderSearch ? (
                  <div className="coffee-header-search">
                    <Input
                      placeholder={t('searchPlaceholder', 'Search your products')}
                      value={searchText}
                      disabled={shouldHideHeaderSearch}
                      onChange={(event) => {
                        if (shouldHideHeaderSearch) return;
                        const value = event.target.value;
                        setSearchText(value);
                        if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
                        searchTimeoutRef.current = setTimeout(() => {
                          onSearch(value);
                        }, 300);
                      }}
                      onPressEnter={() => {
                        if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
                        onSearch(searchText);
                      }}
                      prefix={
                        <SearchOutlined
                          style={{ color: 'var(--coffee-app-muted, rgba(32, 18, 15, 0.62))' }}
                        />
                      }
                      aria-label="Search products"
                      allowClear
                    />
                  </div>
                ) : null}

                <Space size={12} className="coffee-header-actions">
                  <Badge count={wishlistCount} size="small" offset={[-6, 6]}>
                    <Link to="/wishlist" className="coffee-icon-link" aria-label="Wishlist">
                      <HeartOutlined />
                    </Link>
                  </Badge>

                  <Badge count={lineCount} size="small" offset={[-6, 6]}>
                    <button
                      type="button"
                      className="coffee-icon-link coffee-cart-icon-btn"
                      aria-label={t('cart', 'Cart')}
                      onClick={() => {
                        if (isMobile) {
                          navigate('/cart');
                        } else {
                          setCartOpen(true);
                        }
                      }}
                    >
                      <ShoppingCartOutlined />
                    </button>
                  </Badge>

                  {isAuthenticated ? (
                    <Dropdown
                      menu={{ items: accountItems, onClick: onAccountClick }}
                      trigger={['click']}
                    >
                      <button
                        type="button"
                        className="coffee-account-chip"
                        aria-label="Account menu"
                      >
                        <Avatar size={36} style={getAvatarStyles(displayName)}>
                          {displayName[0]?.toUpperCase()}
                        </Avatar>
                      </button>
                    </Dropdown>
                  ) : (
                    <Button
                      className="coffee-signup-button"
                      type="primary"
                      onClick={() => {
                        setAuthMode('login');
                      }}
                    >
                      {t('login', 'Login')}
                    </Button>
                  )}
                </Space>
              </>
            ) : (
              <>
                <button
                  type="button"
                  className={
                    hasSelectedMobileLocation
                      ? 'coffee-delivery-pill coffee-delivery-pill-mobile coffee-mobile-address is-selected'
                      : 'coffee-delivery-pill coffee-delivery-pill-mobile coffee-mobile-address'
                  }
                  aria-label={t('changeStore', 'Change store')}
                  onClick={openStoreSelector}
                >
                  <EnvironmentFilled className="coffee-delivery-pill__pin" aria-hidden="true" />
                  <span>
                    <strong>{mobileLocationLabel || t('selectStore', 'Select store')}</strong>
                  </span>
                  <DownOutlined className="coffee-delivery-pill__chevron" aria-hidden="true" />
                </button>

                {!shouldHideHeaderSearch ? (
                  <button
                    type="button"
                    className={`coffee-icon-link coffee-search-btn ${mobileSearchOpen ? 'active' : ''}`}
                    onClick={() => setMobileSearchOpen((prev) => !prev)}
                    aria-label="Search"
                  >
                    <SearchOutlined />
                  </button>
                ) : null}

                {isAuthenticated ? (
                  <Dropdown
                    menu={{ items: accountItems, onClick: onAccountClick }}
                    trigger={['click']}
                  >
                    <button
                      type="button"
                      className="coffee-mobile-profile-btn"
                      aria-label="Account menu"
                      style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer' }}
                    >
                      <Avatar size={32} style={getAvatarStyles(displayName)}>
                        {displayName[0]?.toUpperCase()}
                      </Avatar>
                    </button>
                  </Dropdown>
                ) : (
                  <button
                    type="button"
                    className="coffee-mobile-profile-btn"
                    onClick={() => {
                      setAuthMode('login');
                    }}
                    style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer' }}
                    aria-label="Login"
                  >
                    <Avatar
                      size={36}
                      style={{
                        color: 'var(--color-text-secondary, currentColor)',
                      }}
                      icon={<UserOutlined />}
                    />
                  </button>
                )}

                <div
                  className={`coffee-search ${mobileSearchOpen ? 'open' : ''} ${
                    shouldHideHeaderSearch ? 'is-hidden' : ''
                  }`}
                >
                  <Input
                    placeholder={t('searchPlaceholder', 'Search your products')}
                    value={searchText}
                    disabled={shouldHideHeaderSearch}
                    onChange={(event) => {
                      if (shouldHideHeaderSearch) return;
                      const value = event.target.value;
                      setSearchText(value);
                      if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
                      searchTimeoutRef.current = setTimeout(() => {
                        onSearch(value);
                      }, 300);
                    }}
                    onPressEnter={() => {
                      if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
                      onSearch(searchText);
                    }}
                    prefix={
                      <SearchOutlined
                        style={{ color: 'var(--coffee-app-muted, rgba(32, 18, 15, 0.62))' }}
                      />
                    }
                    aria-label="Search products"
                    allowClear
                  />
                </div>
              </>
            )}
          </div>
        </div>
      </Header>

      {showMobileTabs ? (
        <nav className="coffee-mobile-tabs" aria-label="Mobile navigation">
          {/* Left 2 tabs: Home, Menu */}
          {mobileTabs.slice(0, 2).map((tab) => {
            const active = isTabActive(tab);
            return (
              <Link
                key={tab.key}
                to={tab.to}
                onClick={(e) => {
                  if (tab.onClick) {
                    e.preventDefault();
                    tab.onClick();
                  } else if (tab.to === '/orders' && !isAuthenticated) {
                    e.preventDefault();
                    onOrdersClick();
                  }
                }}
                className={`coffee-mobile-tab ${active ? 'active' : ''}`}
              >
                <span className="coffee-mobile-tab-icon">
                  {active && tab.activeIcon ? tab.activeIcon : tab.icon}
                </span>
                <span className="coffee-mobile-tab-label">{tab.label}</span>
                {active ? <span className="coffee-active-indicator" /> : null}
              </Link>
            );
          })}

          {/* Center Floating Coffee Cup Badge -> Catalog Page */}
          <Link
            to="/catalog"
            className={`coffee-mobile-center-badge ${pathname.startsWith('/catalog') ? 'active' : ''}`}
            aria-label={t('catalog', 'Catalog')}
          >
            <div className="coffee-badge-inner">
              <svg
                width="44"
                height="44"
                viewBox="0 0 44 44"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <defs>
                  {/* Coffee color */}
                  <linearGradient id="coffeeCup" x1="8" y1="14" x2="34" y2="31">
                    <stop offset="0%" stopColor="#6B3F24" />
                    <stop offset="50%" stopColor="#4E2B18" />
                    <stop offset="100%" stopColor="#2F170D" />
                  </linearGradient>

                  {/* Coffee inside cup */}
                  <radialGradient id="coffeeSurface" cx="50%" cy="40%" r="70%">
                    <stop offset="0%" stopColor="#8A5430" />
                    <stop offset="55%" stopColor="#4E2B18" />
                    <stop offset="100%" stopColor="#291209" />
                  </radialGradient>

                  {/* Cup highlight */}
                  <linearGradient id="cupHighlight" x1="0" y1="0" x2="1" y2="0">
                    <stop offset="0%" stopColor="#FFFFFF" stopOpacity="0.45" />
                    <stop offset="100%" stopColor="#FFFFFF" stopOpacity="0" />
                  </linearGradient>

                  {/* Steam fade */}
                  <linearGradient id="steam" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#d36810" stopOpacity="0.15" />
                    <stop offset="50%" stopColor="#ca8b16" stopOpacity="0.75" />
                    <stop offset="100%" stopColor="#FFFFFF" stopOpacity="0.05" />
                  </linearGradient>

                  {/* Soft shadow */}
                  <filter id="cupShadow" x="-30%" y="-30%" width="160%" height="160%">
                    <feDropShadow
                      dx="0"
                      dy="2"
                      stdDeviation="1.8"
                      floodColor="#000000"
                      floodOpacity="0.35"
                    />
                  </filter>
                </defs>

                {/* =========================
        STEAM / HEAT
    ========================= */}

                <path
                  d="M14 13C11.8 10.5 15.5 8.7 13.8 5.8"
                  stroke="url(#steam)"
                  strokeWidth="1.7"
                  strokeLinecap="round"
                />

                <path
                  d="M21 13C18.7 10.2 22.5 8.5 20.7 5.2"
                  stroke="url(#steam)"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                />

                <path
                  d="M28 13C25.7 10.5 29.5 8.6 27.8 5.7"
                  stroke="url(#steam)"
                  strokeWidth="1.7"
                  strokeLinecap="round"
                />

                {/* Small heat particles */}
                <circle cx="11.5" cy="8" r="0.7" fill="#c48636" opacity="0.35" />
                <circle cx="31.5" cy="9" r="0.6" fill="#df7819" opacity="0.3" />

                {/* =========================
        SAUCER
    ========================= */}

                <ellipse cx="21" cy="33" rx="14" ry="3.2" fill="#24130C" opacity="0.8" />

                <ellipse cx="21" cy="32.5" rx="12.5" ry="2.5" fill="#6B3F24" opacity="0.65" />

                {/* =========================
        CUP + HANDLE
    ========================= */}

                <g filter="url(#cupShadow)">
                  {/* Handle outer */}
                  <path
                    d="M30 17.5H32.5C35 17.5 36.5 19.3 36.5 21.5C36.5 24 34.8 25.7 32.5 25.7H30"
                    fill="none"
                    stroke="#4E2B18"
                    strokeWidth="3"
                    strokeLinecap="round"
                  />

                  {/* Handle inner highlight */}
                  <path
                    d="M31 19.5H32.3C33.7 19.5 34.5 20.4 34.5 21.6C34.5 23 33.6 23.8 32.3 23.8H31"
                    fill="none"
                    stroke="#8A5430"
                    strokeWidth="1"
                    strokeLinecap="round"
                  />

                  {/* Main cup */}
                  <path
                    d="M9 15.5H30.5V24.5C30.5 29.2 27 31.5 19.8 31.5C12.7 31.5 9 29.2 9 24.5V15.5Z"
                    fill="url(#coffeeCup)"
                  />

                  {/* Cup upper rim */}
                  <ellipse cx="19.75" cy="15.5" rx="10.75" ry="3.2" fill="#7A4728" />

                  {/* Coffee surface */}
                  <ellipse cx="19.75" cy="15.1" rx="9.1" ry="2.25" fill="url(#coffeeSurface)" />

                  {/* Coffee crema */}
                  <path
                    d="M13 15C15 13.9 17.5 14.1 19 15C20.7 16 23 16 26 14.8"
                    stroke="#A86A3C"
                    strokeWidth="0.7"
                    strokeLinecap="round"
                    opacity="0.7"
                  />

                  {/* Cup front highlight */}
                  <path
                    d="M11.5 18V24.2C11.5 26.6 13 28.3 15.2 29"
                    stroke="url(#cupHighlight)"
                    strokeWidth="1.6"
                    strokeLinecap="round"
                  />

                  {/* Cup bottom shadow */}
                  <path
                    d="M11.5 27.8C15.5 30.2 24.5 30.5 28.5 27.7"
                    stroke="#1E0D07"
                    strokeWidth="1"
                    opacity="0.6"
                  />
                </g>

                {/* =========================
        COFFEE BEANS
    ========================= */}

                <g opacity="0.9">
                  {/* Left bean */}
                  <ellipse
                    cx="10"
                    cy="35.5"
                    rx="2.7"
                    ry="1.7"
                    transform="rotate(-25 10 35.5)"
                    fill="#4E2B18"
                  />

                  <path
                    d="M8.5 35.8C9.5 34.8 10.5 35 11.5 35.8"
                    stroke="#9A6037"
                    strokeWidth="0.6"
                    strokeLinecap="round"
                  />

                  {/* Right bean */}
                  <ellipse
                    cx="30"
                    cy="35.5"
                    rx="2.7"
                    ry="1.7"
                    transform="rotate(25 30 35.5)"
                    fill="#4E2B18"
                  />

                  <path
                    d="M28.5 35.8C29.5 35 30.5 34.8 31.5 35.8"
                    stroke="#9A6037"
                    strokeWidth="0.6"
                    strokeLinecap="round"
                  />
                </g>
              </svg>
            </div>
          </Link>

          {/* Right 2 tabs: Favorites, Orders */}
          {mobileTabs.slice(2, 4).map((tab) => {
            const active = isTabActive(tab);
            return (
              <Link
                key={tab.key}
                to={tab.to}
                onClick={(e) => {
                  if (tab.onClick) {
                    e.preventDefault();
                    tab.onClick();
                  } else if (tab.to === '/orders' && !isAuthenticated) {
                    e.preventDefault();
                    onOrdersClick();
                  }
                }}
                className={`coffee-mobile-tab ${active ? 'active' : ''}`}
              >
                <span className="coffee-mobile-tab-icon">
                  {tab.key === 'favorites' ? (
                    <Badge count={wishlistCount} size="small" offset={[2, -2]}>
                      {active && tab.activeIcon ? tab.activeIcon : tab.icon}
                    </Badge>
                  ) : active && tab.activeIcon ? (
                    tab.activeIcon
                  ) : (
                    tab.icon
                  )}
                </span>
                <span className="coffee-mobile-tab-label">{tab.label}</span>
                {active ? <span className="coffee-active-indicator" /> : null}
              </Link>
            );
          })}

          {/* Coffee Leaf Right Decor */}
        </nav>
      ) : null}

      <Drawer
        className="coffee-cart-drawer"
        title={
          <span className="coffee-drawer-title">
            <ShoppingCartOutlined />
            <strong>{t('yourCart', 'Your Cart')}</strong>
            {!isCartEmpty && (
              <small>
                ({lineCount} {lineCount === 1 ? t('item', 'Item') : t('items', 'Items')})
              </small>
            )}
          </span>
        }
        extra={
          lineCount > 0 ? (
            <Button
              className="coffee-drawer-clear-all-button"
              type="text"
              size="small"
              onClick={() => clearCart.mutate()}
              loading={clearCart.isPending}
            >
              {t('clearAll', 'Clear all')}
            </Button>
          ) : null
        }
        open={cartOpen}
        onClose={() => setCartOpen(false)}
        width={isCartDrawerViewport ? 500 : '100%'}
        placement="right"
        maskClosable
        keyboard
        zIndex={2000}
      >
        <div className="grocery-drawer-layout">
          <div className="grocery-drawer-lines">
            {isCartEmpty ? (
              <EmptyState
                title={t('yourCartIsEmpty', 'Your cart is empty')}
                description={t(
                  'addItemsToGetStarted',
                  'Explore our menu and add items to your cart.',
                )}
              />
            ) : (
              visibleCartItems.map((line) => (
                <article className="grocery-drawer-line cart-drawer-line" key={line.lineId}>
                  <ImageWithFallback
                    src={line.imageUrl}
                    alt={line.itemName}
                    aspectRatio="1 / 1"
                    radius={10}
                    fallbackSrc={coffeePlaceholderSrc}
                  />
                  <div className="grocery-drawer-line-main">
                    <CartLineDetails line={line} t={t} />
                  </div>
                  <div className="grocery-drawer-line-actions">
                    <button
                      type="button"
                      aria-label={t('edit', 'Edit') + ` ${line.itemName}`}
                      onClick={() => setEditingCartLine(line)}
                    >
                      <EditOutlined />
                    </button>
                    <button
                      type="button"
                      aria-label={t('remove', 'Remove') + ` ${line.itemName}`}
                      onClick={() => removeCartLine.mutate(line.lineId)}
                    >
                      <DeleteOutlined />
                    </button>
                  </div>
                  <div className="grocery-drawer-stepper">
                    <button
                      type="button"
                      disabled={line.quantity <= 1}
                      onClick={() =>
                        updateCartLine.mutate({
                          lineId: line.lineId,
                          quantity: line.quantity - 1,
                          notes: line.notes,
                        })
                      }
                    >
                      −
                    </button>
                    <span>{line.quantity}</span>
                    <button
                      type="button"
                      onClick={() =>
                        updateCartLine.mutate({
                          lineId: line.lineId,
                          quantity: line.quantity + 1,
                          notes: line.notes,
                        })
                      }
                    >
                      +
                    </button>
                  </div>
                  <CartLineNote
                    value={line.notes}
                    onSave={(notes) => saveNote(line, notes)}
                    isSaving={isSavingNote(line)}
                    t={t}
                  />
                </article>
              ))
            )}
            {hasHiddenCartLines ? (
              <div className="grocery-drawer-lines-toggle">
                <Button
                  type="default"
                  block
                  onClick={() => setShowAllCartLines((value) => !value)}
                  aria-expanded={showAllCartLines}
                >
                  {showAllCartLines
                    ? t('showFewerItems', 'Show fewer items')
                    : t('showAllItems', `Show all items (${cartItems.length})`)}
                </Button>
              </div>
            ) : null}
          </div>

          {!isCartEmpty && (
            <div className="grocery-drawer-bottom">
              <div className="grocery-drawer-total">
                <div>
                  <span>{t('totalItems', 'Total items')}</span>
                  <strong>{cartItems.length}</strong>
                </div>
                <div>
                  <span>{t('totalItemsQuantity', 'Total items quantity')}</span>
                  <strong>{totalItemQuantity}</strong>
                </div>
                <div>
                  <span>{t('subtotal', 'Subtotal')}</span>
                  <Price amount={cart?.subtotal ?? 0} />
                </div>
                <div>
                  <span>{t('tax', 'Tax')}</span>
                  <Price amount={cart?.taxTotal ?? 0} />
                </div>
                <div className="grocery-drawer-divider" />
                <div className="grocery-drawer-grand-total">
                  <strong>{t('total', 'Total')}</strong>
                  <Price amount={cart?.grandTotal ?? 0} size={22} />
                </div>
                <Button
                  type="primary"
                  size="large"
                  block
                  disabled={isCartEmpty}
                  loading={isCheckoutLoading}
                  onMouseEnter={loadCheckoutModule}
                  onClick={openCheckout}
                >
                  {t('proceedToCheckout', 'Proceed to Checkout')}
                </Button>
              </div>
            </div>
          )}
        </div>
      </Drawer>
      <AppModal
        kind="product"
        open={Boolean(editingCartLine)}
        footer={null}
        onCancel={() => setEditingCartLine(null)}
        afterClose={releaseCartLine}
        width={isCartDrawerViewport ? 780 : 'calc(100% - 2rem)'}
        centered={!isCartDrawerViewport}
        destroyOnHidden
        zIndex={4100}
        className="coffee-product-modal product-detail-modal"
        styles={{
          body: { padding: 0 },
          content: {
            padding: 0,
            overflow: 'hidden',
            borderRadius: 6,
            background: 'var(--bg-surface, var(--bg-card, #ffffff))',
            maxHeight: isCartDrawerViewport ? '80vh' : '100vh',
          },
        }}
      >
        {shownCartLine ? (
          <ProductDetailView
            productId={shownCartLine.itemId}
            inModal
            cartLine={shownCartLine}
            onDone={() => setEditingCartLine(null)}
          />
        ) : null}
      </AppModal>
      <StorefrontAuthModal
        open={Boolean(authMode)}
        mode={authMode ?? 'choice'}
        onCancel={() => {
          setAuthMode(null);
          setAuthRedirect(null);
        }}
        onAuthenticated={() => {
          if (authRedirect === '__checkout_modal__') {
            setCartOpen(false);
            setCheckoutOpen(true);
            setAuthRedirect(null);
            return;
          }
          if (authRedirect) navigate(authRedirect);
          setAuthRedirect(null);
        }}
      />
      <AppModal
        kind="checkout"
        open={checkoutOpen}
        footer={null}
        onCancel={() => setCheckoutOpen(false)}
        width={isCartDrawerViewport ? 1180 : '100%'}
        destroyOnHidden
        className="default-checkout-modal coffee-checkout-modal"
        styles={{ body: { padding: 0 }, content: { padding: 0, overflow: 'hidden' } }}
      >
        <Suspense
          fallback={
            <div className="default-checkout-loading coffee-checkout-loading">
              Loading checkout...
            </div>
          }
        >
          <CheckoutPage inModal />
        </Suspense>
      </AppModal>
    </>
  );
}
